import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Chip, Collapse, Typography } from '@mui/material';
import { FactCheck } from '@mui/icons-material';
import { BackButton } from '../components/back-button';
import { FilterableDataTable } from '../components/filterable-data-table';
import { EligibilityWorkqueueStats } from '../components/eligibility-workqueue-stats';
import { EligibilityOverrideForm } from '../components/eligibility-override-form';
import { useGetAdminEligibilityListQuery } from '../store/hooks';
import { formatRelativeTime } from '../utils/formatting';
import type { AdminEligibilityItem } from '../types/eligibility';

const STATUS_CHIP_COLORS: Record<string, 'error' | 'warning' | 'success' | 'default'> = {
  RED: 'error',
  YELLOW: 'warning',
  GREEN: 'success',
};

const ACTION_LABELS: Record<string, string> = {
  portal_review: 'Portal Review',
  phone_verify: 'Phone Verify',
  block: 'Block',
  none: 'None',
};

const FILTERS = [
  {
    key: 'status',
    label: 'Status',
    type: 'select' as const,
    options: [
      { value: 'RED', label: 'RED' },
      { value: 'YELLOW', label: 'YELLOW' },
      { value: 'GREEN', label: 'GREEN' },
    ],
  },
  {
    key: 'next_action',
    label: 'Action Needed',
    type: 'select' as const,
    options: [
      { value: 'portal_review', label: 'Portal Review' },
      { value: 'phone_verify', label: 'Phone Verify' },
      { value: 'block', label: 'Block' },
    ],
  },
];

export default function EligibilityWorkqueue() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // Read URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('perPage') || '20', 10);
  const status = searchParams.get('status') || undefined;
  const nextAction = searchParams.get('next_action') || undefined;
  const search = searchParams.get('search') || undefined;
  const sortBy = searchParams.get('sortBy') || 'created_at';
  const sortOrder = (searchParams.get('sortOrder') || 'desc') as 'asc' | 'desc';

  const { data: apiData, isLoading } = useGetAdminEligibilityListQuery({
    page,
    perPage,
    nextActionOwner: 'biller',
    status,
    nextAction,
    search,
    sortBy,
    sortOrder,
  });

  const data = apiData;

  const selectedItem = useMemo(
    () => data?.items.find(i => i.id === selectedItemId) ?? null,
    [data, selectedItemId],
  );

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handlePageChange = useCallback(
    (newPage: number) => updateParams({ page: newPage.toString() }),
    [updateParams],
  );

  const handlePerPageChange = useCallback(
    (newPerPage: number) => updateParams({ perPage: newPerPage.toString(), page: '1' }),
    [updateParams],
  );

  const handleFiltersChange = useCallback(
    (filters: Record<string, string>) => {
      const updates: Record<string, string | undefined> = {
        page: '1',
        status: filters.status || undefined,
        next_action: filters.next_action || undefined,
        search: filters.search || undefined,
      };
      updateParams(updates);
    },
    [updateParams],
  );

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
      updateParams({ sortBy: newSortBy, sortOrder: newSortOrder });
    },
    [updateParams],
  );

  const handleRowClick = useCallback(
    (row: AdminEligibilityItem) => {
      setSelectedItemId(prev => (prev === row.id ? null : row.id));
    },
    [],
  );

  const columns = useMemo(
    () => [
      {
        key: 'status' as const,
        label: 'Status',
        render: (value: unknown) => {
          const s = String(value || '');
          return (
            <Chip
              label={s}
              size="small"
              color={STATUS_CHIP_COLORS[s] ?? 'default'}
              sx={{ minWidth: 70, fontWeight: 600 }}
            />
          );
        },
      },
      {
        key: 'patient_name' as const,
        label: 'Patient',
        render: (value: unknown, row: AdminEligibilityItem) => (
          <span className="font-medium text-gray-900">
            {String(value || row.patient_id || 'Unknown')}
          </span>
        ),
      },
      {
        key: 'payer_name' as const,
        label: 'Payer',
        render: (value: unknown) => (
          <span className="text-gray-700">{String(value || '-')}</span>
        ),
      },
      {
        key: 'next_action' as const,
        label: 'Action Needed',
        render: (value: unknown) => {
          const action = String(value || '');
          return action && action !== 'none' ? (
            <Chip
              label={ACTION_LABELS[action] ?? action}
              size="small"
              variant="outlined"
              color="primary"
            />
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
      },
      {
        key: 'alerts' as const,
        label: 'Alerts',
        sortable: false,
        render: (value: unknown) => {
          const alerts = value as string[] | undefined;
          if (!alerts || alerts.length === 0) return <span className="text-gray-400">-</span>;
          return (
            <Chip
              label={`${alerts.length} alert${alerts.length > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          );
        },
      },
      {
        key: 'expires_at' as const,
        label: 'Expires',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const dateStr = String(value);
          const isPast = new Date(dateStr) < new Date();
          return (
            <span className={isPast ? 'text-red-600 font-medium' : 'text-gray-700'}>
              {formatRelativeTime(dateStr)}
            </span>
          );
        },
      },
      {
        key: 'organization_name' as const,
        label: 'Organization',
        render: (value: unknown) => (
          <span className="text-gray-500">{String(value || '-')}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton />

        <div className="flex items-center gap-3 mt-4 mb-6">
          <FactCheck className="text-blue-600" fontSize="large" />
          <Typography variant="h4" className="font-bold text-gray-900">
            Eligibility Workqueue
          </Typography>
        </div>

        <EligibilityWorkqueueStats data={data} loading={isLoading} />

        <FilterableDataTable<AdminEligibilityItem>
          title="Eligibility Cases"
          columns={columns}
          data={data?.items ?? []}
          total={data?.total}
          loading={isLoading}
          page={page}
          perPage={perPage}
          filters={FILTERS}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          emptyMessage="No eligibility cases in the queue"
        />

        <Collapse in={!!selectedItem} unmountOnExit>
          {selectedItem && (
            <EligibilityOverrideForm
              key={selectedItem.id}
              item={selectedItem}
              onCollapse={() => setSelectedItemId(null)}
            />
          )}
        </Collapse>
      </main>
    </div>
  );
}
